import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Slide,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useSelector } from 'react-redux';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const formatDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const Field = ({ icon, label, value }) => (
    <div>
        <div className="tw-flex tw-items-center tw-gap-1 tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-gray-400 tw-font-semibold tw-mb-0.5">
            {icon ? <Icon icon={icon} className="tw-text-rose-500 tw-text-[11px]" /> : null}
            <span>{label}</span>
        </div>
        <div className="tw-text-xs tw-text-gray-800 tw-font-medium tw-break-words">
            {value || '—'}
        </div>
    </div>
);

const StatusPill = ({ value }) => {
    const isActive = value === 1 || value === true || String(value).toUpperCase() === 'ACTIVE';
    return (
        <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-medium tw-border ${
            isActive
                ? 'tw-bg-emerald-50 tw-text-emerald-600 tw-border-emerald-100'
                : 'tw-bg-gray-50 tw-text-gray-500 tw-border-gray-100'
        }`}>
            <span className={`tw-w-1.5 tw-h-1.5 tw-rounded-full ${isActive ? 'tw-bg-emerald-500' : 'tw-bg-gray-400'}`} />
            {isActive ? 'Active' : 'Inactive'}
        </span>
    );
};

const TypePill = ({ value }) => {
    if (!value) return <span className="tw-text-gray-400">—</span>;
    return (
        <span className="tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-full tw-bg-indigo-50 tw-text-indigo-600 tw-text-[10px] tw-font-medium">
            {value}
        </span>
    );
};

const SocietyDetailsModal = ({ open, handleClose, society }) => {
    const { communityDetails } = useSelector((s) => s.CtrlCommunityManagementReducers || {});
    const { brandData } = useSelector((s) => s.CtrlBrandReducers || {});

    // Prefer redux details (fetched on view click); fall back to the row data passed in.
    const data = communityDetails || society || {};

    const id = data.society_id || society?.id;
    const name = data.name || society?.name;
    const type = data.type || society?.type;
    const registrationNumber = data.registration_number;
    const registrationAuthority = data.registration_authority;
    const societyCode = data.society_code;
    const brandId = data.brand_id;
    // status (ACTIVE | INACTIVE | …) is the source of truth — is_active stays 1
    // for inactive / under-review / suspended societies.
    const status = data.status || society?.status;
    const createdAt = data.created_at || society?.createdOn;

    const brand = brandId
        ? (brandData?.data || []).find((b) => b.brand_id === brandId)
        : null;
    const brandName = brand?.name || (brandId ? `#${brandId}` : null);

    const users = Array.isArray(data.users) ? data.users : [];

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            TransitionComponent={Transition}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2, maxWidth: 520 } }}
        >
            {/* Clean header — rendered as a div: DialogTitle's <h2> picks up the
                theme's large heading styles and blows the title up */}
            <DialogTitle component="div" sx={{ p: 0 }}>
                <Box sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider',
                }}>
                    <div className="tw-text-sm tw-font-semibold tw-text-gray-800">Community Details</div>
                    <IconButton aria-label="close" onClick={handleClose} size="small" sx={{ color: 'text.secondary', p: 0.5 }}>
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0, '&.MuiDialogContent-root': { paddingTop: 0 } }}>
                {/* Identity strip */}
                <div className="tw-px-4 tw-py-2.5 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-gap-2.5 tw-bg-rose-50/30">
                    <div className="tw-w-9 tw-h-9 tw-rounded-full tw-bg-rose-100 tw-flex tw-items-center tw-justify-center tw-text-rose-600 tw-font-bold tw-text-sm tw-flex-shrink-0">
                        {(name || 'C').charAt(0).toUpperCase()}
                    </div>
                    <div className="tw-min-w-0 tw-flex-1">
                        <div className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-truncate">{name || '—'}</div>
                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-mt-0.5 tw-flex-wrap">
                            <span className="tw-text-[10px] tw-text-gray-400 tw-font-mono">#{societyCode || id}</span>
                            <TypePill value={type} />
                            <StatusPill value={status} />
                        </div>
                    </div>
                </div>

                {/* Field grid — mirrors the create/edit form */}
                <div className="tw-px-4 tw-py-3 tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-3">
                    <Field icon="solar:buildings-bold" label="Community Name" value={name} />
                    <Field icon="solar:tag-bold" label="Property Type" value={type} />
                    <Field icon="solar:document-text-bold" label="Registration Number" value={registrationNumber} />
                    <Field icon="solar:shield-check-bold" label="Registration Authority" value={registrationAuthority} />
                    <Field icon="solar:medal-ribbon-bold" label="Brand" value={brandName} />
                    <Field icon="solar:calendar-add-bold" label="Created On" value={formatDate(createdAt)} />
                </div>

                {/* Assigned users — only when data is present */}
                {users.length > 0 && (
                    <div className="tw-border-t tw-border-gray-100 tw-px-4 tw-py-3">
                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-2">
                            <Icon icon="solar:users-group-rounded-bold" className="tw-text-rose-500 tw-text-sm" />
                            <div className="tw-text-xs tw-font-semibold tw-text-gray-800">Assigned Users</div>
                            <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-full tw-bg-rose-50 tw-text-rose-600 tw-text-[10px] tw-font-medium">
                                {users.length}
                            </span>
                        </div>
                        <div className="tw-space-y-2">
                            {users.map((row, idx) => {
                                const u = row?.user || {};
                                const fullName = [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || '—';
                                return (
                                    <div key={row?.user_society_id || `${u.user_id}-${idx}`} className="tw-flex tw-items-center tw-gap-2 tw-text-xs">
                                        <div className="tw-w-7 tw-h-7 tw-rounded-full tw-bg-rose-50 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-text-rose-600 tw-font-semibold tw-text-[10px]">
                                            {(u.first_name || u.email || '?').charAt(0).toUpperCase()}
                                        </div>
                                        <div className="tw-min-w-0 tw-flex-1">
                                            <div className="tw-font-medium tw-text-gray-800 tw-truncate">{fullName}</div>
                                            <div className="tw-text-[10px] tw-text-gray-400 tw-truncate">{u.email || '—'}</div>
                                        </div>
                                        {row?.role?.role_name ? (
                                            <span className="tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-full tw-bg-indigo-50 tw-text-indigo-600 tw-text-[10px] tw-font-medium">
                                                {row.role.role_name}
                                            </span>
                                        ) : null}
                                        {row?.is_primary ? (
                                            <Icon icon="solar:star-bold" className="tw-text-rose-500 tw-text-xs" title="Primary" />
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 2, py: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
                <button
                    type="button"
                    className="tw-h-8 tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-600 tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50"
                    onClick={handleClose}
                >
                    <Icon icon="solar:close-circle-outline" className="tw-text-sm" />
                    <span>Close</span>
                </button>
            </DialogActions>
        </Dialog>
    );
};

export default SocietyDetailsModal;
